import { useEffect, useState } from "react";
import { api, fileUrl, GALLERY_LABELS, GALLERY_ORDER, galleryCategory, isVideo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GripVertical, ChevronUp, ChevronDown, Save } from "lucide-react";

function Group({ cat, items: initial }) {
  const [items, setItems] = useState(initial);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => setItems(initial), [initial]);

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    setItems((arr) => { const n = [...arr]; const [m] = n.splice(from, 1); n.splice(to, 0, m); return n; });
  };
  const save = async () => {
    setSaving(true);
    try { await api.post("/gallery/reorder", { ids: items.map((g) => g.id) }); toast.success("Ordine salvato"); }
    catch (e) { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-brass/15 p-5" data-testid={`gallery-order-${cat}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-serif text-xl text-bordeaux">{GALLERY_LABELS[cat]}</h3>
        <Button onClick={save} disabled={saving || items.length === 0} size="sm" className="bg-bordeaux hover:bg-bordeaux-dark text-cream shrink-0" data-testid={`gallery-order-save-${cat}`}>
          <Save className="w-4 h-4 mr-1" />{saving ? "Salvataggio…" : "Salva ordine"}
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((g, i) => (
          <div key={g.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIdx !== null && dragIdx !== i) move(dragIdx, i); setDragIdx(null); }} onDragEnd={() => setDragIdx(null)}
            className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${dragIdx === i ? "border-bordeaux bg-bordeaux/5 opacity-70" : "border-brass/15 bg-cream-dark/40"}`}
            data-testid={`gallery-order-row-${g.id}`}>
            <GripVertical className="w-4 h-4 text-oak/30 cursor-grab shrink-0" />
            <span className="w-5 text-center text-sm text-brass-hover font-serif shrink-0">{i + 1}</span>
            {isVideo(g.image)
              ? <video src={fileUrl(g.image)} muted className="w-12 h-12 rounded object-cover shrink-0 bg-oak-dark" />
              : <img src={fileUrl(g.image)} alt="" className="w-12 h-12 rounded object-cover shrink-0" />}
            <span className="flex-1 min-w-0 truncate text-oak">{g.title || g.caption || g.image_alt || "Senza titolo"}</span>
            <div className="flex flex-col shrink-0">
              <button onClick={() => move(i, i - 1)} disabled={i === 0} className="text-oak/50 hover:text-bordeaux disabled:opacity-20" data-testid={`gallery-order-up-${g.id}`}><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, i + 1)} disabled={i === items.length - 1} className="text-oak/50 hover:text-bordeaux disabled:opacity-20" data-testid={`gallery-order-down-${g.id}`}><ChevronDown className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-oak/50 text-sm py-4 text-center">Nessuna foto in questo gruppo.</p>}
      </div>
    </div>
  );
}

export default function GalleryOrderManager() {
  const [all, setAll] = useState([]);
  useEffect(() => { api.get("/gallery?all=true").then((r) => setAll(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-bordeaux">Ordine delle foto in Galleria</h2>
        <p className="text-sm text-oak/60 mt-1">Trascina le foto (o usa le frecce) per decidere l'ordine in cui compaiono in ogni gruppo, poi premi <strong>Salva ordine</strong>.</p>
      </div>
      {GALLERY_ORDER.map((c) => (
        <Group key={c} cat={c} items={all.filter((g) => galleryCategory(g.category) === c)} />
      ))}
    </div>
  );
}
